import { type CSSProperties, type FC, type ReactNode } from 'preact/compat';

import WebApp from '@twa-dev/sdk';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';

import { Lottie } from '@/shared/ui/lottie';

import duckLottie from '@/assets/lotties/duck.lottie?url';

interface Props {
  className?: string;
  title?: ReactNode;
  message?: ReactNode;
  buttonLabel?: ReactNode;
  onClickButton?: () => void;
}

interface Styles {
  container: CSSProperties;
  title: CSSProperties;
  message: CSSProperties;
  link: CSSProperties;
  lottie: CSSProperties;
}

const FONT_VARIANTS = `
  ui-rounded,
  -apple-system,
  BlinkMacSystemFont,
  'Segoe UI',
  Roboto,
  Oxygen,
  Ubuntu,
  Cantarell,
  'Open Sans',
  'Helvetica Neue',
  sans-serif
`;

const getStyles = (): Styles => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    width: '100vw',
    height: '100vh',
    padding: '40px 28px',
    gap: '8px',
    overflow: 'auto',
    backgroundColor: WebApp?.themeParams?.bg_color || 'var(--bg_color, #ffffff)',
  },
  title: {
    color: WebApp?.themeParams?.text_color || 'var(--text_color, #222222)',
    textAlign: 'center',
    fontSize: '22px',
    fontWeight: 600,
    lineHeight: '28px',
    letterSpacing: 0,
    fontFamily: FONT_VARIANTS,
  },
  message: {
    color: WebApp?.themeParams?.hint_color || 'var(--hint_color, #888888)',
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '19px',
    letterSpacing: -0.23,
    marginTop: 10,
  },
  link: {
    color: WebApp?.themeParams?.link_color || 'var(--link_color, #2678b6)',
    textAlign: 'center',
    marginTop: '22px',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: 0,
    fontFamily: FONT_VARIANTS,
  },
  lottie: { height: '112px', width: '112px' },
});

export const ErrorPage: FC<Props> = ({
  title,
  message,
  buttonLabel,
  onClickButton = () => window.location.reload(),
}) => {
  const t = useIntl();

  const styles = getStyles();

  return (
    <div style={styles.container}>
      <Lottie src={duckLottie} style={styles.lottie} loop autoplay />

      <div style={styles.title}>{title ?? t('Something went wrong')}</div>
      {!!message && <div style={styles.message}>{message}</div>}

      <a onClick={onClickButton} style={styles.link}>
        {buttonLabel ?? t('Reload Page')}
      </a>
    </div>
  );
};
